/* Writer Service: isolates Excel column writes */
/* global Excel */
import { Constants } from "../constants/constants";
async function getActiveRow(context) {
    const sel = context.workbook.getSelectedRange();
    sel.load("rowIndex");
    await context.sync();
    return sel.rowIndex;
}
async function getSelectedRows(context) {
    const sel = context.workbook.getSelectedRange();
    sel.load(["rowIndex", "rowCount"]);
    await context.sync();
    const start = sel.rowIndex;
    const count = sel.rowCount || 1;
    return Array.from({ length: count }, (_, i) => start + i);
}
export async function writePractices(practiceNames, practiceIds) {
    if (!practiceNames.length && !practiceIds.length) {
        return { row: -1, columns: [], message: "No practices to write" };
    }
    return Excel.run(async (context) => {
        const row = await getActiveRow(context);
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.getCell(row, Constants.SITE_PRACTICE_COLUMNS.PRACTICE_NAME).values = [
            [practiceNames.join("; ")],
        ];
        sheet.getCell(row, Constants.SITE_PRACTICE_COLUMNS.PRACTICE_ID).values = [
            [practiceIds.join("; ")],
        ];
        await context.sync();
        return {
            row: row + 1,
            columns: [
                Constants.SITE_PRACTICE_COLUMNS.PRACTICE_NAME,
                Constants.SITE_PRACTICE_COLUMNS.PRACTICE_ID,
            ],
            message: "Practices written",
        };
    });
}
export async function writeSites(siteNames, siteIds) {
    if (!siteNames.length && !siteIds.length) {
        return { row: -1, columns: [], message: "No sites to write" };
    }
    return Excel.run(async (context) => {
        const row = await getActiveRow(context);
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.getCell(row, Constants.SITE_PRACTICE_COLUMNS.SITE_NAME).values = [[siteNames.join("; ")]];
        sheet.getCell(row, Constants.SITE_PRACTICE_COLUMNS.SITE_ID).values = [[siteIds.join("; ")]];
        await context.sync();
        return {
            row: row + 1,
            columns: [Constants.SITE_PRACTICE_COLUMNS.SITE_NAME, Constants.SITE_PRACTICE_COLUMNS.SITE_ID],
            message: "Sites written",
        };
    });
}
export async function writeBoth(practiceNames, practiceIds, siteNames, siteIds) {
    if (!practiceNames.length && !practiceIds.length && !siteNames.length && !siteIds.length) {
        return { rows: [], columns: [], message: "Nothing to write" };
    }
    return Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const sel = context.workbook.getSelectedRange();
        sel.load(["rowIndex", "rowCount"]);
        await context.sync();
        const startRow = sel.rowIndex;
        const rowCount = sel.rowCount || 1;
        if (rowCount > Constants.MAX_ROWS_WRITE) {
            return {
                rows: [],
                columns: [],
                message: `Selection exceeds max batch (${rowCount} > ${Constants.MAX_ROWS_WRITE}). Aborted`,
            };
        }
        const pNameVal = practiceNames.join("; ");
        const pIdVal = practiceIds.join("; ");
        const sNameVal = siteNames.join("; ");
        const sIdVal = siteIds.join("; ");
        // Write practice columns as a single range (G-H or G only, etc.)
        if (practiceNames.length || practiceIds.length) {
            const pNameRange = sheet.getRangeByIndexes(startRow, Constants.SITE_PRACTICE_COLUMNS.PRACTICE_NAME, rowCount, 1);
            const pIdRange = sheet.getRangeByIndexes(startRow, Constants.SITE_PRACTICE_COLUMNS.PRACTICE_ID, rowCount, 1);
            pNameRange.values = Array.from({ length: rowCount }, () => [pNameVal]);
            pIdRange.values = Array.from({ length: rowCount }, () => [pIdVal]);
        }
        if (siteNames.length || siteIds.length) {
            const sNameRange = sheet.getRangeByIndexes(startRow, Constants.SITE_PRACTICE_COLUMNS.SITE_NAME, rowCount, 1);
            const sIdRange = sheet.getRangeByIndexes(startRow, Constants.SITE_PRACTICE_COLUMNS.SITE_ID, rowCount, 1);
            sNameRange.values = Array.from({ length: rowCount }, () => [sNameVal]);
            sIdRange.values = Array.from({ length: rowCount }, () => [sIdVal]);
        }
        await context.sync();
        const rows = Array.from({ length: rowCount }, (_, i) => startRow + i);
        return {
            rows: rows.map((r) => r + 1),
            columns: [
                ...(practiceNames.length || practiceIds.length
                    ? [
                        Constants.SITE_PRACTICE_COLUMNS.PRACTICE_NAME,
                        Constants.SITE_PRACTICE_COLUMNS.PRACTICE_ID,
                    ]
                    : []),
                ...(siteNames.length || siteIds.length
                    ? [Constants.SITE_PRACTICE_COLUMNS.SITE_NAME, Constants.SITE_PRACTICE_COLUMNS.SITE_ID]
                    : []),
            ],
            message: `Written to ${rowCount} row${rowCount === 1 ? "" : "s"}`,
        };
    });
}
export async function writeRoles(roleNames, roleIds) {
    console.log("writeRoles called", { roleNames, roleIds });
    if (!roleNames.length && !roleIds.length) {
        console.log("No roles to write, returning empty result");
        return { rows: [], columns: [], message: "No roles to write" };
    }
    return Excel.run(async (context) => {
        console.log("Excel.run started for writeRoles");
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const sel = context.workbook.getSelectedRange();
        sel.load(["rowIndex", "rowCount"]);
        await context.sync();
        const startRow = sel.rowIndex;
        const rowCount = sel.rowCount || 1;
        console.log("Selection info", { startRow, rowCount });
        if (rowCount > Constants.MAX_ROWS_WRITE) {
            console.log("Selection exceeds max batch");
            return {
                rows: [],
                columns: [],
                message: `Selection exceeds max batch (${rowCount} > ${Constants.MAX_ROWS_WRITE}). Aborted`,
            };
        }
        const rNameVal = roleNames.join("; ");
        const rIdVal = roleIds.join("; ");
        console.log("Role values prepared", { rNameVal, rIdVal });
        console.log("Writing to columns", {
            nameCol: Constants.ROLE_COLUMNS.ROLE_NAME,
            idCol: Constants.ROLE_COLUMNS.ROLE_ID,
        });
        // Write as contiguous ranges instead of cell-by-cell
        const nameRange = sheet.getRangeByIndexes(startRow, Constants.ROLE_COLUMNS.ROLE_NAME, rowCount, 1);
        const idRange = sheet.getRangeByIndexes(startRow, Constants.ROLE_COLUMNS.ROLE_ID, rowCount, 1);
        console.log("Ranges prepared", {
            nameRange: `row ${startRow}, col ${Constants.ROLE_COLUMNS.ROLE_NAME}`,
            idRange: `row ${startRow}, col ${Constants.ROLE_COLUMNS.ROLE_ID}`,
        });
        nameRange.values = Array.from({ length: rowCount }, () => [rNameVal]);
        idRange.values = Array.from({ length: rowCount }, () => [rIdVal]);
        console.log("Values set, calling context.sync()");
        await context.sync();
        console.log("context.sync() completed");
        const rows = Array.from({ length: rowCount }, (_, i) => startRow + i);
        const result = {
            rows: rows.map((r) => r + 1),
            columns: [Constants.ROLE_COLUMNS.ROLE_NAME, Constants.ROLE_COLUMNS.ROLE_ID],
            message: `Roles written to ${rowCount} row${rowCount === 1 ? "" : "s"}`,
        };
        console.log("writeRoles completed successfully", result);
        return result;
    });
}
export async function clearRoles() {
    return Excel.run(async (context) => {
        const sel = context.workbook.getSelectedRange();
        sel.load(["rowIndex", "rowCount"]);
        await context.sync();
        const startRow = sel.rowIndex;
        const rowCount = sel.rowCount || 1;
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        if (rowCount > Constants.MAX_ROWS_WRITE) {
            return {
                row: startRow + 1,
                columns: [],
                message: `Clear exceeds max batch (${rowCount} > ${Constants.MAX_ROWS_WRITE}). Aborted`,
            };
        }
        // Clear ONLY the two role columns (F and K) separately.
        // - F (index 5) = Role Name
        // - K (index 10) = Role ID
        // DO NOT clear a contiguous F-K range because columns G,H,I,J contain practice/site data.
        // Also use ClearApplyTo.contents so formatting (data validation, color, etc.) is preserved.
        const roleNameRange = sheet.getRangeByIndexes(startRow, Constants.ROLE_COLUMNS.ROLE_NAME, rowCount, 1);
        const roleIdRange = sheet.getRangeByIndexes(startRow, Constants.ROLE_COLUMNS.ROLE_ID, rowCount, 1);
        roleNameRange.clear(Excel.ClearApplyTo.contents);
        roleIdRange.clear(Excel.ClearApplyTo.contents);
        await context.sync();
        return {
            row: startRow + 1,
            columns: [Constants.ROLE_COLUMNS.ROLE_NAME, Constants.ROLE_COLUMNS.ROLE_ID],
            message: `Roles cleared for ${rowCount} row${rowCount === 1 ? "" : "s"}`,
        };
    });
}
export async function clearRow(all = true) {
    return Excel.run(async (context) => {
        const sel = context.workbook.getSelectedRange();
        sel.load(["rowIndex", "rowCount"]);
        await context.sync();
        const startRow = sel.rowIndex;
        const rowCount = sel.rowCount || 1;
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const startCol = Constants.SITE_PRACTICE_COLUMNS.PRACTICE_NAME;
        const colCount = all
            ? Constants.SITE_PRACTICE_COLUMNS.SITE_ID - Constants.SITE_PRACTICE_COLUMNS.PRACTICE_NAME + 1
            : 2;
        if (rowCount > Constants.MAX_ROWS_WRITE) {
            return {
                row: startRow + 1,
                columns: [],
                message: `Clear exceeds max batch (${rowCount} > ${Constants.MAX_ROWS_WRITE}). Aborted`,
            };
        }
        // Clear as a single contiguous range instead of row-by-row
        const range = sheet.getRangeByIndexes(startRow, startCol, rowCount, colCount);
        range.clear();
        await context.sync();
        return {
            row: startRow + 1,
            columns: [...Array(colCount)].map((_, i) => startCol + i),
            message: `Cleared ${rowCount} row${rowCount === 1 ? "" : "s"}`,
        };
    });
}
